import { OrderTypeEnum } from '../../shared/types/order-types';

export interface FieldDestinations {
  pdf: boolean;
  freeVacancies: boolean;
}

export interface OrderFieldContext {
  orderType: string;
  displayPlannedTermForProvision: boolean;
  passSalaryDescriptionToTemplate: boolean;
  exportFreetextRequirements: boolean;
}

export const DEFAULT_ORDER_FIELD_CONTEXT: OrderFieldContext = {
  orderType: OrderTypeEnum.staffing,
  displayPlannedTermForProvision: false,
  passSalaryDescriptionToTemplate: false,
  exportFreetextRequirements: false,
};

export const NO_DESTINATIONS: FieldDestinations = {
  pdf: false,
  freeVacancies: false,
};

interface Ctx extends OrderFieldContext {
  isStaffing: boolean;
  isSelection: boolean;
  isJobmatch: boolean;
}

type Rule = (c: Ctx) => FieldDestinations;

const uses = (pdf: boolean, freeVacancies: boolean): FieldDestinations => ({
  pdf,
  freeVacancies,
});

// Keeps the literal keys (for the OrderField type) while type-checking every rule.
const defineRules = <T extends Record<string, Rule>>(rules: T): T => rules;

/**
 * Where each order form field ends up: the order PDF sent to the client
 * (boris/templates/orders/document_*.html) and/or the Free vacancies view.
 * Fields that are used in neither place are not listed.
 */
const ORDER_FIELD_DESTINATIONS = defineRules({
  // Header
  orderType: () => uses(true, true),
  trafficGeneration: () => uses(false, true),
  country: () => uses(false, true),
  language: () => uses(true, false),
  orderNumber: (c: Ctx) => uses(c.isStaffing || c.isJobmatch, false),
  subtype: (c: Ctx) => uses(c.isStaffing, false),

  // Step 1: client and contact info
  company: () => uses(true, true),
  region: () => uses(false, true),
  projectManager: () => uses(true, false),
  contract: () => uses(true, false),
  biuroSignee: () => uses(true, false),
  clientContact: () => uses(true, true),
  additionalContacts: () => uses(true, true),
  isLogoPublicAllowed: (c: Ctx) => uses(c.isSelection, true),
  isWorkplaceVisitAllowed: () => uses(false, true),
  isCompanyInternational: () => uses(false, true),
  isCompanyModern: () => uses(false, true),
  companyDescription: () => uses(false, true),
  cancellationPeriod: () => uses(true, false),
  compensationForRecruitment: () => uses(true, false),
  otherInfo: () => uses(true, false),
  minVsdfPayment: () => uses(true, false),
  workTimeRegistrationSystem: () => uses(true, false),
  remunerationAmount: () => uses(true, false),
  plannedTermForProvision: (c: Ctx) =>
    uses(c.displayPlannedTermForProvision, false),
  provisionOfInformationOnConclusion: () => uses(true, false),
  warrantyPeriod: () => uses(true, false),

  // Step 2: client need
  salaryGross: () => uses(true, true),
  salaryNet: () => uses(false, true),
  salaryUseInDocument: () => uses(true, false),
  salaryDescription: (c: Ctx) =>
    uses(c.passSalaryDescriptionToTemplate && !c.isJobmatch, false),
  canIncreaseSalary: () => uses(false, true),
  canGetBonuses: () => uses(false, true),
  periodDates: () => uses(true, true),
  periodDemand: (c: Ctx) => uses(c.isStaffing, true),
  periodIndefinite: (c: Ctx) => uses(c.isStaffing || c.isJobmatch, true),
  shortage: (c: Ctx) => uses(c.isSelection, true),
  indefiniteOrderInfo: (c: Ctx) => uses(c.isStaffing, false),
  orderCanBeExtended: () => uses(false, true),
  canEmployUa: () => uses(false, true),
  candidatesToBeProvided: (c: Ctx) => uses(c.isSelection, false),

  // Step 3: specification
  positionTitle: () => uses(true, true),
  workFunctions: (c: Ctx) => uses(!c.isJobmatch, true),
  additionalRecruitementInformation: () => uses(false, true),
  intensity: () => uses(false, true),
  onboardingDuration: (c: Ctx) => uses(c.isSelection, true),
  careerPossibilities: (c: Ctx) => uses(c.isSelection, false),
  scheduleTypes: () => uses(false, true),
  scheduleText: (c: Ctx) => uses(!c.isJobmatch, true),
  address: (c: Ctx) => uses(!c.isJobmatch, true),
  transportTags: (c: Ctx) => uses(c.isSelection, true),
  healthcheckType: () => uses(false, true),
  riskFactors: (c: Ctx) => uses(c.isStaffing, true),
  requirementsOther: (c: Ctx) => uses(c.isSelection, false),
  minExperienceMonths: () => uses(false, true),
  experienceType: () => uses(false, true),
  ageRestriction: () => uses(false, true),
  eligibleStaff: () => uses(false, true),
  requirementLists: (c: Ctx) =>
    uses(
      c.isStaffing || (c.isSelection && !c.exportFreetextRequirements),
      true,
    ),
  requirementsFreetext: (c: Ctx) =>
    uses(c.isSelection && c.exportFreetextRequirements, false),
  certificates: (c: Ctx) => uses(c.isStaffing, true),
  // "provision" tag group: client requires CV / client interviews candidates / work fair
  provisionTags: () => uses(false, true),
  workClothing: (c: Ctx) => uses(c.isStaffing, false),
  workplaceBenefits: () => uses(false, true),
  isWorkplaceVideoAllowed: () => uses(false, true),
});

export type OrderField = keyof typeof ORDER_FIELD_DESTINATIONS;

export function getFieldDestinations(
  field: OrderField,
  context: OrderFieldContext,
): FieldDestinations {
  const rule: Rule = ORDER_FIELD_DESTINATIONS[field];
  if (!rule) {
    return NO_DESTINATIONS;
  }
  return rule({
    ...context,
    isStaffing: context.orderType === OrderTypeEnum.staffing,
    isSelection:
      context.orderType === OrderTypeEnum.selection ||
      context.orderType === OrderTypeEnum.partialSelection,
    isJobmatch: context.orderType === OrderTypeEnum.jobmatch,
  });
}
